import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { SkillsSection } from './sections/SkillsSection';
import { ExperienceSection } from './sections/ExperienceSection';
import { LanguagesSection } from './sections/LanguagesSection';
import { ProjectsSection } from './sections/ProjectsSection';
import { CertificationsSection } from './sections/CertificationsSection';
import { AttachmentsSection } from './sections/AttachmentsSection';
import styles from './ExpertProfileTab.module.css';

interface ExpertExperience {
  id: string;
  area: string;
  years: number;
  level?: string | null;
}

interface ExpertLanguage {
  id: string;
  language: string;
  level: string;
}

interface ExpertProject {
  id: string;
  fromMonth: number;
  fromYear: number;
  toMonth?: number | null;
  toYear?: number | null;
  isCurrent: boolean;
  role: string;
  tasks?: string | null;
  company?: string | null;
  companySize?: string | null;
  industry?: string | null;
}

interface ExpertCertification {
  id: string;
  title: string;
  issuingBody: string;
  website?: string | null;
  issueYear: number;
}

interface AttachmentMeta {
  id: string;
  filename: string;
  mimetype: string;
  size: number;
  createdAt: string;
}

interface ExpertProfile {
  id: string;
  skills: string[];
  experiences: ExpertExperience[];
  languages: ExpertLanguage[];
  projects: ExpertProject[];
  certifications: ExpertCertification[];
  attachments: AttachmentMeta[];
}

export type { ExpertExperience, ExpertLanguage, ExpertProject, ExpertCertification, AttachmentMeta };

interface ExpertProfileTabProps {
  apiBase?: string;
}

export function ExpertProfileTab({ apiBase = '/expert-profile/me' }: ExpertProfileTabProps = {}) {
  const [profile, setProfile] = useState<ExpertProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get<ExpertProfile>(apiBase);
      setProfile(data);
      setError('');
    } catch {
      setError('Experten-Profil konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleExport = async (format: 'pdf' | 'docx') => {
    setExporting(true);
    try {
      const response = await api.get(`${apiBase}/export/${format}`, {
        responseType: 'blob',
      });
      const blob = response.data as Blob;
      const disposition = response.headers['content-disposition'] as string | undefined;
      const utf8Match = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = disposition?.match(/filename="([^"]+)"/i);
      const filename = utf8Match
        ? decodeURIComponent(utf8Match[1])
        : plainMatch?.[1] ?? (format === 'pdf' ? 'Expertenprofil.pdf' : 'Expertenprofil.docx');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError(`Export als ${format.toUpperCase()} fehlgeschlagen`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        Experten-Profil wird geladen...
      </div>
    );
  }

  if (error && !profile) {
    return <div className={styles.errorBox}>{error}</div>;
  }

  if (!profile) return null;

  const [activeTab, setActiveTab] = useState<'competencies' | 'projects' | 'certifications'>('competencies');

  const tabs = [
    { key: 'competencies' as const, label: 'Kompetenzen', count: profile.skills.length + profile.languages.length + profile.experiences.length },
    { key: 'projects' as const, label: 'Projekthistorie', count: profile.projects.length },
    { key: 'certifications' as const, label: 'Nachweise', count: profile.certifications.length + profile.attachments.length },
  ];

  return (
    <div className={styles.expertContainer}>
      {/* Export-Buttons */}
      <div className={styles.exportBar}>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => handleExport('pdf')}
          disabled={exporting}
        >
          {exporting ? 'Exportiert...' : 'PDF Export'}
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => handleExport('docx')}
          disabled={exporting}
        >
          {exporting ? 'Exportiert...' : 'Word Export'}
        </button>
      </div>

      {/* Tab-Navigation */}
      <div className={styles.profileTabBar}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${styles.profileTab} ${activeTab === tab.key ? styles.profileTabActive : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span className={styles.profileTabCount}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Tab: Kompetenzen */}
      {activeTab === 'competencies' && (
        <div className={styles.threeColumnRow}>
          <SkillsSection skills={profile.skills} onUpdate={loadProfile} apiBase={apiBase} />
          <LanguagesSection languages={profile.languages} onUpdate={loadProfile} apiBase={apiBase} />
          <ExperienceSection experiences={profile.experiences} onUpdate={loadProfile} apiBase={apiBase} />
        </div>
      )}

      {/* Tab: Projekthistorie */}
      {activeTab === 'projects' && (
        <ProjectsSection projects={profile.projects} onUpdate={loadProfile} apiBase={apiBase} />
      )}

      {/* Tab: Nachweise */}
      {activeTab === 'certifications' && (
        <div className={styles.twoColumnRow}>
          <CertificationsSection certifications={profile.certifications} onUpdate={loadProfile} apiBase={apiBase} />
          <AttachmentsSection attachments={profile.attachments} onUpdate={loadProfile} />
        </div>
      )}
    </div>
  );
}
